import type { PhotoboothImage, PhotoboothPromptMetadata, PricingSetting, PricingSettingInput } from '../types';
import { createLogger } from '../utils/logger';
import { API_BASE, apiFetch } from '../utils/api';

const log = createLogger('Settings');

// Built-in pricing defaults from API docs (April 2026) used when no DB settings exist.
// Per-call prices reflect medium quality at our typical output sizes (~3840x2560).
// Token prices are from official API docs per 1M tokens.
const BUILTIN_PRICING: PricingSetting[] = [
  // FAL endpoints — moondream detection (VLM usage)
  { id: -1,  provider: 'fal', endpoint_or_model: 'fal-ai/moondream3',                 price_per_call: null, price_per_input_token: 0.40, price_per_output_token: 3.50,  updated_at: null },
  { id: -10, provider: 'fal', endpoint_or_model: 'fal-ai/moondream3-preview/detect',  price_per_call: null, price_per_input_token: 0.40, price_per_output_token: 3.50,  updated_at: null },
  // FAL endpoints — image generation
  { id: -2, provider: 'fal', endpoint_or_model: 'fal-ai/nano-banana-2/edit', price_per_call: 0.16, price_per_input_token: null, price_per_output_token: null,  updated_at: null },
  { id: -3, provider: 'fal', endpoint_or_model: 'openai/gpt-image-2/edit',   price_per_call: 0.14, price_per_input_token: 8.00, price_per_output_token: 30.00, updated_at: null },
  { id: -4, provider: 'fal', endpoint_or_model: 'fal-ai/gpt-image-1.5/edit', price_per_call: 0.05, price_per_input_token: 8.00, price_per_output_token: 10.00, updated_at: null },
  // OpenAI direct API — cost tracked via direct_cost from backend; per-call as fallback
  { id: -5, provider: 'openai', endpoint_or_model: 'openai_gpt_image_2',     price_per_call: 0.14,  price_per_input_token: null, price_per_output_token: null, updated_at: null },
  { id: -6, provider: 'openai', endpoint_or_model: 'openai_gpt_image_1_5',   price_per_call: 0.05,  price_per_input_token: null, price_per_output_token: null, updated_at: null },
  // OpenRouter vision (VLM usage)
  { id: -11, provider: 'fal', endpoint_or_model: 'openrouter/router/vision',  price_per_call: null,  price_per_input_token: 2.00, price_per_output_token: 8.00, updated_at: null },
  // Gemini models (token-based)
  { id: -7, provider: 'gemini', endpoint_or_model: 'gemini-3-pro-image-preview',     price_per_call: null, price_per_input_token: 2.00, price_per_output_token: 12.00, updated_at: null },
  { id: -8, provider: 'gemini', endpoint_or_model: 'gemini-3.1-flash-image-preview', price_per_call: null, price_per_input_token: 0.50, price_per_output_token: 3.00,  updated_at: null },
  { id: -9, provider: 'gemini', endpoint_or_model: 'gemini-2.5-flash',               price_per_call: null, price_per_input_token: 0.15, price_per_output_token: 0.60,  updated_at: null },
];

/**
 * Merge DB-saved settings with built-in defaults. DB settings take precedence.
 */
function mergeWithDefaults(dbSettings: PricingSetting[]): PricingSetting[] {
  const merged = [...dbSettings];
  const savedEndpoints = new Set(dbSettings.map(s => s.endpoint_or_model));
  for (const builtin of BUILTIN_PRICING) {
    if (!savedEndpoints.has(builtin.endpoint_or_model)) {
      merged.push(builtin);
    }
  }
  return merged;
}

/**
 * Fetch all pricing settings
 */
export async function fetchPricingSettings(): Promise<PricingSetting[]> {
  log.debug('Fetching pricing settings');
  const response = await apiFetch('/api/settings/pricing');
  if (!response.ok) {
    log.error(`Failed to fetch pricing settings: ${response.status}`);
    throw new Error(`Failed to fetch pricing settings: ${response.statusText}`);
  }
  const dbSettings: PricingSetting[] = await response.json();
  return mergeWithDefaults(dbSettings);
}

/**
 * Update pricing settings (batch upsert)
 */
export async function updatePricingSettings(
  settings: PricingSettingInput[]
): Promise<PricingSetting[]> {
  const response = await apiFetch('/api/settings/pricing', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ settings }),
  });

  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to update pricing settings: ${response.status}`, error);
    throw new Error(error || 'Failed to update pricing settings');
  }

  log.info(`Updated ${settings.length} pricing settings`);
  return response.json();
}

/**
 * Fetch all photobooth images
 */
export async function fetchPhotobooths(): Promise<PhotoboothImage[]> {
  log.debug('Fetching photobooths');
  const response = await apiFetch('/api/settings/photobooths');
  if (!response.ok) {
    log.error(`Failed to fetch photobooths: ${response.status}`);
    throw new Error(`Failed to fetch photobooths: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Upload a new photobooth image
 */
export async function uploadPhotobooth(name: string, file: File): Promise<PhotoboothImage> {
  const formData = new FormData();
  formData.append('name', name);
  formData.append('file', file);

  const response = await apiFetch('/api/settings/photobooths', {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to upload photobooth '${name}': ${response.status}`, error);
    throw new Error(error || 'Failed to upload photobooth');
  }
  log.info(`Uploaded photobooth '${name}'`);
  return response.json();
}

/**
 * Delete a photobooth image
 */
export async function deletePhotobooth(name: string): Promise<void> {
  log.debug(`Deleting photobooth '${name}'`);
  const response = await apiFetch(`/api/settings/photobooths/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to delete photobooth '${name}': ${response.status}`, error);
    throw new Error(error || 'Failed to delete photobooth');
  }
  log.info(`Deleted photobooth '${name}'`);
}

/**
 * Set the default photobooth
 */
export async function setDefaultPhotobooth(name: string): Promise<PhotoboothImage> {
  log.debug(`Setting default photobooth to '${name}'`);
  const response = await apiFetch('/api/settings/photobooths/default', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to set default photobooth: ${response.status}`, error);
    throw new Error(error || 'Failed to set default photobooth');
  }
  log.info(`Set default photobooth to '${name}'`);
  return response.json();
}

/**
 * Get the URL for a photobooth image thumbnail
 */
export function getPhotoboothImageUrl(name: string): string {
  return `${API_BASE}/api/settings/photobooths/${encodeURIComponent(name)}/image`;
}

/**
 * Update prompt metadata for a photobooth image
 */
export async function updatePhotoboothMetadata(
  name: string,
  metadata: PhotoboothPromptMetadata
): Promise<PhotoboothPromptMetadata> {
  const response = await apiFetch(
    `/api/settings/photobooths/${encodeURIComponent(name)}/metadata`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(metadata),
    }
  );

  if (!response.ok) {
    const error = await response.text();
    log.error(`Failed to update metadata for '${name}': ${response.status}`, error);
    throw new Error(error || 'Failed to update photobooth metadata');
  }
  log.info(`Updated metadata for photobooth '${name}'`);
  return response.json();
}

export interface ConfigYamlResponse {
  path: string | null;
  content: string | null;
  found: boolean;
}

/**
 * Fetch the config.yaml that the backend loaded at startup
 */
export async function fetchConfigYaml(): Promise<ConfigYamlResponse> {
  log.debug('Fetching loaded config.yaml');
  const response = await apiFetch('/api/settings/config-yaml');
  if (!response.ok) {
    log.error(`Failed to fetch config.yaml: ${response.status}`);
    throw new Error(`Failed to fetch config.yaml: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Calculate cost from FAL and Gemini usage
 */
export function calculateCost(
  falUsage: Record<string, { endpoint: string; calls: number; input_tokens: number; output_tokens: number; direct_cost?: number }> | null,
  geminiUsage: Record<string, { model: string; calls: number; input_tokens: number; output_tokens: number }> | null,
  pricingSettings: PricingSetting[]
): number {
  let totalCost = 0;
  
  // Calculate FAL/OpenAI costs (OpenAI direct API calls are also tracked under fal_usage)
  if (falUsage) {
    for (const [endpoint, usage] of Object.entries(falUsage)) {
      // Priority: 1) direct_cost from API (most accurate), 2) price_per_call estimate, 3) token-based pricing
      if (usage.direct_cost && usage.direct_cost > 0) {
        totalCost += usage.direct_cost;
      } else {
        const setting = pricingSettings.find(
          s => s.endpoint_or_model === endpoint
        );
        if (setting?.price_per_call && setting.price_per_call > 0) {
          totalCost += usage.calls * setting.price_per_call;
        } else if (setting) {
          if (setting.price_per_input_token) {
            totalCost += usage.input_tokens * (setting.price_per_input_token / 1_000_000);
          }
          if (setting.price_per_output_token) {
            totalCost += usage.output_tokens * (setting.price_per_output_token / 1_000_000);
          }
        }
      }
    }
  }
  
  // Calculate Gemini costs (prices stored as per-1M tokens)
  if (geminiUsage) {
    for (const [model, usage] of Object.entries(geminiUsage)) {
      const setting = pricingSettings.find(
        s => s.provider === 'gemini' && s.endpoint_or_model === model
      );
      if (setting) {
        if (setting.price_per_input_token) {
          totalCost += usage.input_tokens * (setting.price_per_input_token / 1_000_000);
        }
        if (setting.price_per_output_token) {
          totalCost += usage.output_tokens * (setting.price_per_output_token / 1_000_000);
        }
      }
    }
  }
  
  return totalCost;
}

/**
 * Format cost as USD string
 */
export function formatCost(cost: number): string {
  if (cost === 0) return '-';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}
